"use client";

/**
 * Presentational read-only banner shown while an admin is impersonating a user
 * via the "Log in as user" tool. Renders nothing for normal sessions.
 *
 * This is purely a signal — the API enforces read-only independently by
 * rejecting writes on sessions whose token carries the `impersonated_by` claim.
 * Each app supplies the values from its own session source (NextAuth session
 * for exams/open, Firebase ID-token claims for the teacher dashboard).
 */
type Props = {
  /** Email of the admin impersonating, or null/undefined for a normal session. */
  impersonatedBy?: string | null;
  /** Email of the user being viewed, if known. */
  viewingAs?: string | null;
  /** True when the admin started a writable session (editing enabled). */
  writable?: boolean | null;
};

export default function ImpersonationBanner({
  impersonatedBy,
  viewingAs,
  writable,
}: Props) {
  if (!impersonatedBy) return null;

  // Distinct colours so the mode is never ambiguous: dark red = locked
  // read-only; amber = editing IS enabled (changes affect the real account).
  const background = writable ? "#b45309" : "#7f1d1d";
  const target = viewingAs ? ` ${viewingAs}` : " this user";
  const message = writable
    ? `Editing enabled — changes you make affect ${viewingAs ?? "this user"}'s account.`
    : "Read-only — changes are disabled.";

  return (
    <div
      role="status"
      style={{
        width: "100%",
        background,
        color: "#fff",
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: 600,
        textAlign: "center",
        lineHeight: 1.4,
      }}
    >
      {`Impersonating${target}`}. {message}
      {` (Started by ${impersonatedBy}.)`}
    </div>
  );
}
